import { onlyUnique } from 'lib/util/array';

import { compareNumbers, upper } from './util';
import makePrice, { Price } from './Price';
import { ValueListItem } from './Tc.Module.EcommerceProductDataHub';

export const SIMPLE_PAY_DUMMY_SUBSCRIPTION = 'Installment-Subscription';

export const enum Case {
	Retention = 'RETENTION',
	Activation = 'ACTIVATION',
	New = 'NEW',
}

export interface SubscriptionData {
	subscriptionName? : string;
	subscriptionId : string;
	useCase? : Case;
	recurringCharge? : number;
	duration? : number | string;
	price : number;
	simplePayRate? : number;
	sortSubscription? : number;

}

// Temporary hack to get rid of inappropriate subscriptions
function hackyShouldShowSubscription(subscription : Subscription) {
	if (/^N_/.test(subscription.getId())) {
		// N_ subscriptions will be deprecated and only the new NG_ subscriptions will be sold in the future (AUG 2017)
		return false;
	}

	const segment = $(document.body).data('page-segment');

	if(segment === 'res' && / Pro /.test(subscription.getId())) {
		// Subscription not in RES according to naming convention
		return false;
	}
	if(segment === 'sme' && /^NG_LEGO/.test(subscription.getId()) && !/ Pro /.test(subscription.getId())) {
		// Subscription not in SME according to naming convention
		return false;
	}
	if(segment === 'res' && /-data-pro-/.test(subscription.getId())) {
		// Data subscription not in RES according to naming convention
		return false;
	}
	if(segment === 'sme' && /^NG_inOne-data-/.test(subscription.getId()) && !/-data-pro-/.test(subscription.getId())) {
		// Data subscription not in SME according to naming convention
		return false;
	}

	return true;
}

export interface SubscriptionValueListItem extends ValueListItem {
	sortSubscription : number;
}

export function compareSubscriptions(sub1 : SubscriptionValueListItem, sub2 : SubscriptionValueListItem) {
	// FIXME NEC: Is sortSubscription still to be sorted in descending order (as globalRank was)?
	return compareNumbers(sub2.sortSubscription || 0, sub1.sortSubscription || 0);
}

export class Subscription {
	private data : SubscriptionData;
	constructor(data : SubscriptionData) {
		this.data = {...data};
	}
	public getName() {
		return this.data.subscriptionName || this.getId();
	}
	public getId() {
		return this.data.subscriptionId;
	}
	public getRecurringCharge() {
		return makePrice(this.data.recurringCharge || 0);
	}
	public getCase() {
		return upper(this.data.useCase || Case.Activation);
	}
	public getDuration() {
		return Number(this.data.duration);
	}
	public getPrice() {
		return makePrice(this.data.price);
	}
	public getSimplePayRate() {
		return makePrice(this.data.simplePayRate || 0);
	}
	public getSortSubscription() {
		return this.data.sortSubscription || -0;
	}
	public matches(id? : string, duration? : string | number, kaze? : Case) {
		// Special-case id === '' to not match because it’s the default fallback initial ID
		if(id === '' || (id && this.getId() !== id)) {
			// ID does not match
			return false;
		}

		// We always have to get a case, default to Activation
		kaze = upper(kaze || Case.Activation) as Case;
		if(this.getCase() !== kaze) {
			// Case does not match
			return false;
		}

		if(duration && this.getDuration() !== Number(duration)) {
			// Duration given and does not match
			return false;
		}

		if(!('QUnit' in window) && !hackyShouldShowSubscription(this)) {
			return false;
		}

		if(this.getId() === SIMPLE_PAY_DUMMY_SUBSCRIPTION) {
			return false;
		}

		return true;
	}
}

/**
 * Wrapper object for the subscriptions list of one product
 */
export class Subscriptions {
	private subscriptions : Subscription[];
	constructor(subs : SubscriptionData[] = []) {
		this.subscriptions = subs.map(sub => {
			return new Subscription(sub);
		});
	}

	public contains(id? : string, duration? : number | string, kaze? : Case) {
		return this.get(id, duration, kaze) !== null;
	}

	public get(id? : string, duration? : number | string, kaze? : Case) : Subscription | null {
		return this.getAll(id, duration, kaze)[0] || null;
	}

	public getByName(name : string, duration : number, kaze : Case) : Subscription | null {
		return this.subscriptions
			.find(sub => sub.getName() === name && sub.getDuration() === duration && sub.getCase() === upper(kaze)) || null;
	}

	public getAll(id? : string, duration? : number | string, kaze? : Case) {
		return this.subscriptions.filter(function(subscription) {
			return subscription.matches(id, duration, kaze);
		});
	}

	public getDurations(id? : string, kaze? : Case) {
		return this.getAll(id, undefined, kaze)
			.map(function(subscription) {
				return subscription.getDuration();
			})
			.filter(onlyUnique)
			.sort(compareNumbers);
	}

	public getMaxDuration(id? : string, kaze? : Case) : number | null {
		const durations = this.getDurations(id, kaze);
		if(!durations.length) {
			return null;
		}

		return Math.max(...durations);
	}

	public map<T>(callback : (sub : Subscription) => T, duration? : number | string, kaze? : Case) {
		return this.getAll(undefined, duration, kaze).map(callback);
	}

	public getPrice(id? : string, duration? : number | string, kaze? : Case) : Price | null {
		const subscription = this.get(id, duration, kaze);
		if(!subscription) {
			return null;
		}

		return subscription.getPrice();
	}

	public getName(id : string) : string | null {
		const subscription = this.get(id);
		if(!subscription) {
			return null;
		}

		return subscription.getName();
	}

	public getRecurringCharge(id? : string, duration? : number | string, kaze? : Case) : Price | null {
		const subscription = this.get(id, duration, kaze);
		if(!subscription) {
			return null;
		}

		return subscription.getRecurringCharge();
	}

	public getSortSubscription(id? : string, duration? : number | string, kaze? : Case) : number {
		return Math.max.apply(Math, this.getAll(id, duration, kaze).map(function(subscription) {
			return subscription.getSortSubscription();
		})) || -0;
	}

	public getTextValueList(kaze? : Case) : SubscriptionValueListItem[] {
		return this.getAll(undefined, undefined, kaze)
			.map(subscription => {
				return subscription.getId();
			})
			.filter(onlyUnique)
			.map(subscriptionId => {
				return {
					sortSubscription: this.getSortSubscription(subscriptionId, undefined, kaze),
					text: this.getName(subscriptionId)!,
					value: subscriptionId,
				};
			});
	}

	public hasValidSimplePay(kaze? : Case) {
		const subscriptions = this.getAll(undefined, undefined, kaze);
		for(const subscription of subscriptions) {
			if(subscription.getSimplePayRate().value() > 0) {
				return true;
			}
		}

		const price = this.getSimplePayRateWithoutSubscription();

		return price.value() > 0;
	}

	public getSimplePayRateWithoutSubscription() : Price {
		const dummy = this.subscriptions.find(sub => sub.getId() === SIMPLE_PAY_DUMMY_SUBSCRIPTION);
		if (dummy) {
			return dummy.getSimplePayRate();
		}

		return new Price(0);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Subscription.ts